import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { useSchemeCycles } from '../../../../../../features/schemes/network/index';
import {
  useAuction,
  useOpenAuction,
  useSubmitBid,
  useCloseBidding,
  useConfirmWinner,
  useCancelAuction,
} from '../../../../../../features/auctions/network';
import type { AuctionDetail, BidDetail } from '../../../../../../features/auctions/types';
// AuctionAttendanceDrawer expects schemes.api's SubscriberListItem (the wider, canonical shape) --
// not auctions/network's own independently-typed copy of the same /chit-groups/:id/subscribers
// endpoint (see docs/audits/2026-07-22-scheme-finance-reports-audit.md §2.1/§6.2 duplicate-hooks finding).
import { useSchemeSubscribers } from '../../../../../../features/schemes/network/schemes.api';
import { PageHeader } from '../../../../../../components/layout/PageHeader';
import { Button } from '../../../../../../components/ui/Button';
import { Input } from '../../../../../../components/ui/FormField';
import { Card, CardContent } from '../../../../../../components/ui/Card';
import { usePageTitle } from '../../../../../../lib/hooks';
import { Money } from "../../../../../../components/ui/Money";
import Decimal from 'decimal.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../../../lib/locale';
import { formatMoney } from '@repo/utils';

import { useAuctionWebSocket } from './useAuctionWebSocket';
import { AuctionBidForm } from './AuctionBidForm';
import { AuctionBidLog } from './AuctionBidLog';
import { AuctionAttendanceDrawer } from './AuctionAttendanceDrawer';

export const Route = createFileRoute('/_auth/schemes/$id/cycles/$num/auction')({
  component: AuctionPage,
});

function formatPaise(paise: number): string {
  return formatMoney(paise, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE });
}

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open — Accepting Bids',
  BIDDING_CLOSED: 'Bidding Closed',
  WINNER_DETERMINED: 'Winner Determined',
  CONFIRMED: 'Confirmed',
  CANCELLED: 'Cancelled',
};

function AuctionPage() {
  const { id: schemeId, num } = Route.useParams();
  usePageTitle(`Cycle ${num} Auction`);
  const cycleNum = parseInt(num, 10);

  const { data: cycles, isLoading: cyclesLoading } = useSchemeCycles(schemeId);
  const cycle = cycles?.find((c) => c.cycleNumber === cycleNum) ?? null;

  const [localAuctionId, setLocalAuctionId] = useState<string | null>(null);
  const auctionId = localAuctionId ?? cycle?.auctionId ?? null;
  const { data: auctionData, isLoading: auctionLoading, isFetching: auctionPolling, dataUpdatedAt } = useAuction(auctionId);
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);
  const prevUpdateRef = useRef(0);
  useEffect(() => {
    if (dataUpdatedAt && dataUpdatedAt !== prevUpdateRef.current) {
      prevUpdateRef.current = dataUpdatedAt;
      setLastPolledAt(new Date());
    }
  }, [dataUpdatedAt]);

  // Real-time connection state via socket.io — reflects the actual handshake,
  // not a fake always-on indicator.
  const { connected: socketConnected } = useAuctionWebSocket(auctionId);

  const openAuction = useOpenAuction(schemeId);
  const subscribers = useSchemeSubscribers(schemeId);

  const [bidSubscriberId, setBidSubscriberId] = useState('');
  const [bidDiscountRupees, setBidDiscountRupees] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAttendanceDrawer, setShowAttendanceDrawer] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const [docChargeRupees, setDocChargeRupees] = useState('');
  const [taxAmountRupees, setTaxAmountRupees] = useState('');

  const submitBid = useSubmitBid(auctionId ?? '');
  const closeBidding = useCloseBidding(auctionId ?? '');
  const confirmWinner = useConfirmWinner(auctionId ?? '');
  const cancelAuction = useCancelAuction(auctionId ?? '');

  const auction: AuctionDetail | null = auctionData?.auction ?? null;
  const bids: BidDetail[] = auctionData?.bids ?? [];
  const attendeeSubscriberIds: string[] = auctionData?.attendeeSubscriberIds ?? [];

  const sortedBids = [...bids].sort((a, b) => a.bidDiscount - b.bidDiscount);

  const handleOpenAuction = async () => {
    if (!cycle) return;
    setActionError(null);
    try {
      const result = await openAuction.mutateAsync({
        cycleId: cycle.id,
        auctionType: 'OPEN',
      });
      setLocalAuctionId(result.auctionId);
    } catch (e) {
      console.error('Failed to open auction', e);
      setActionError((e as { message?: string }).message || 'Failed to open auction');
    }
  };

  const handleSubmitBid = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError(null);
    const discountPaise = new Decimal(bidDiscountRupees || 0).times(100).round().toNumber();
    try {
      await submitBid.mutateAsync({ subscriberId: bidSubscriberId, bidDiscount: discountPaise });
      setBidSubscriberId('');
      setBidDiscountRupees('');
    } catch (e) {
      console.error('Failed to submit bid', e);
      setActionError((e as { message?: string }).message || 'Failed to submit bid');
    }
  };

  const handleCloseBidding = async () => {
    setActionError(null);
    try {
      await closeBidding.mutateAsync();
    } catch (e) {
      console.error('Failed to close bidding', e);
      setActionError((e as { message?: string }).message || 'Failed to close bidding');
    }
  };

  const handleConfirmWinner = async () => {
    setActionError(null);
    try {
      const docCharge = new Decimal(docChargeRupees || 0).times(100).round().toNumber();
      const taxAmount = new Decimal(taxAmountRupees || 0).times(100).round().toNumber();
      const totalCharge = docCharge + taxAmount;
      await confirmWinner.mutateAsync({ docCharge, taxAmount, totalCharge });
      setShowConfirmModal(false);
    } catch (e) {
      console.error('Failed to confirm winner', e);
      setActionError((e as { message?: string }).message || 'Failed to confirm winner');
    }
  };

  const handleCancelAuction = async () => {
    setActionError(null);
    try {
      await cancelAuction.mutateAsync({ reason: cancelReason });
      setShowCancelModal(false);
      setCancelReason('');
    } catch (e) {
      console.error('Failed to cancel auction', e);
      setActionError((e as { message?: string }).message || 'Failed to cancel auction');
    }
  };

  if (cyclesLoading) {
    return <div className="text-center text-text-secondary text-sm py-12">Loading cycle...</div>;
  }

  if (!cycle) {
    return (
      <div className="text-center py-12">
        <div className="text-danger-text font-semibold">Cycle #{num} not found for this scheme.</div>
        <Link to="/schemes/$id" params={{ id: schemeId }} className="text-accent-700 hover:text-accent-500 font-medium hover:underline text-sm mt-2 block">
          ← Back to scheme
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Cycle #${num} — Auction`}
        subtitle={`Scheduled ${cycle.scheduledDate}`}
        backTo={{ to: "/schemes/$id", params: { id: schemeId } }}
      />

      {actionError && (
        <div className="mb-4 p-3 bg-danger-bg border border-danger-500 rounded-[6px] text-danger-text text-sm">
          {actionError}
        </div>
      )}

      {auctionId && (
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border ${
              socketConnected
                ? 'bg-surface-2 border-border'
                : 'bg-danger-bg border-danger-500'
            }`}
          >
            <span
              className={`h-2.5 w-2.5 rounded-full ${
                socketConnected ? 'bg-success-500 animate-pulse' : 'bg-danger-500'
              }`}
            />
            <span
              className={`font-medium ${
                socketConnected ? 'text-text-secondary' : 'text-danger-text'
              }`}
            >
              {socketConnected ? 'Live Connected' : 'Disconnected'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-border bg-surface-2">
            <span className={`h-2 w-2 rounded-full ${auctionPolling ? 'bg-info-500' : 'bg-text-muted'}`} />
            <span className="font-medium text-text-secondary">
              {auctionPolling ? 'Polling...' : 'Idle'}
            </span>
            {lastPolledAt && (
              <span className="text-text-muted ml-1">
                {Math.floor((Date.now() - lastPolledAt.getTime()) / 1000)}s ago
              </span>
            )}
          </div>
        </div>
      )}

      {/* No auction yet */}
      {!auctionId && !auctionLoading && (
        <NoAuctionState
          onOpen={handleOpenAuction}
          isPending={openAuction.isPending}
        />
      )}

      {auctionLoading && auctionId && (
        <div className="text-text-secondary text-sm">Loading auction...</div>
      )}

      {auction && (
        <>
          <AuctionStatusBadge status={auction.status} />

          {/* OPEN state */}
          {auction.status === 'OPEN' && (
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-surface-2 border border-border rounded-[6px] p-3">
                  <p className="text-xs text-text-muted uppercase tracking-wider font-semibold">Total Bids</p>
                  <p className="text-lg font-bold text-text-primary mt-0.5">{sortedBids.length}</p>
                </div>
                <div className="bg-surface-2 border border-border rounded-[6px] p-3">
                  <p className="text-xs text-text-muted uppercase tracking-wider font-semibold">Lowest Bid</p>
                  <p className="text-lg font-bold text-text-primary mt-0.5 font-mono">
                    {sortedBids.length > 0 ? <Money paise={sortedBids[0].bidDiscount} /> : '—'}
                  </p>
                </div>
                <div className="bg-surface-2 border border-border rounded-[6px] p-3">
                  <p className="text-xs text-text-muted uppercase tracking-wider font-semibold">Highest Bid</p>
                  <p className="text-lg font-bold text-text-primary mt-0.5 font-mono">
                    {sortedBids.length > 0 ? <Money paise={sortedBids[sortedBids.length - 1].bidDiscount} /> : '—'}
                  </p>
                </div>
                <div className="bg-surface-2 border border-border rounded-[6px] p-3">
                  <p className="text-xs text-text-muted uppercase tracking-wider font-semibold">Avg Bid</p>
                  <p className="text-lg font-bold text-text-primary mt-0.5 font-mono">
                    {sortedBids.length > 0
                      ? <Money paise={Math.round(sortedBids.reduce((s, b) => s + b.bidDiscount, 0) / sortedBids.length)} />
                      : '—'}
                  </p>
                </div>
              </div>
              <AuctionBidForm
                subscribers={subscribers.data?.data ?? []}
                bidSubscriberId={bidSubscriberId}
                setBidSubscriberId={setBidSubscriberId}
                bidDiscountRupees={bidDiscountRupees}
                setBidDiscountRupees={setBidDiscountRupees}
                onSubmitBid={handleSubmitBid}
                isSubmittingBid={submitBid.isPending}
                currentLowestBid={sortedBids.length > 0 ? sortedBids[0] : null}
                bidCount={sortedBids.length}
              />
              <AuctionBidLog
                bids={sortedBids}
                winnerSubscriptionId={auction.winnerSubscriptionId}
              />
              <div className="flex justify-between items-center mt-6">
                <Button variant="danger" onClick={() => setShowCancelModal(true)}>
                  Cancel Auction
                </Button>
                <div className="flex items-center gap-3">
                  <Button variant="secondary" onClick={() => setShowAttendanceDrawer(true)}>
                    Manage Attendance
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCloseBidding}
                    disabled={closeBidding.isPending}
                    className="bg-surface-2 hover:bg-surface-3"
                  >
                    {closeBidding.isPending ? 'Closing Bidding…' : 'Close Bidding & Determine Winner'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* BIDDING_CLOSED state (transient) */}
          {auction.status === 'BIDDING_CLOSED' && (
            <div className="mt-4 p-4 bg-warning-bg border border-warning-500 rounded-[6px] text-warning-text font-medium text-sm">
              Bidding closed — determining winner...
            </div>
          )}

          {/* WINNER_DETERMINED state */}
          {auction.status === 'WINNER_DETERMINED' && (
            <WinnerDeterminedState
              auction={auction}
              sortedBids={sortedBids}
              onConfirmClick={() => setShowConfirmModal(true)}
              isConfirming={confirmWinner.isPending}
            />
          )}

          {/* CONFIRMED state */}
          {auction.status === 'CONFIRMED' && (
            <ConfirmedState auction={auction} sortedBids={sortedBids} />
          )}

          {/* CANCELLED state */}
          {auction.status === 'CANCELLED' && (
            <div className="mt-4 p-4 bg-danger-bg border border-danger-500 rounded-[6px] text-sm space-y-3">
              <div>
                <p className="font-semibold text-danger-text">Auction Cancelled</p>
                {auction.cancelledReason && (
                  <p className="text-danger-text/90 mt-1">Reason: {auction.cancelledReason}</p>
                )}
              </div>
              <div className="pt-2 border-t border-danger-500/30 flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleOpenAuction}
                  disabled={openAuction.isPending}
                >
                  {openAuction.isPending ? 'Opening...' : 'Open Replacement Auction'}
                </Button>
              </div>
            </div>
          )}

          <AuctionAttendanceDrawer
            isOpen={showAttendanceDrawer}
            onClose={() => setShowAttendanceDrawer(false)}
            auctionId={auction.id}
            subscribers={subscribers.data?.data ?? []}
            initialAttendees={attendeeSubscriberIds}
          />
        </>
      )}

      {/* Confirm winner modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-surface-1 p-6 rounded shadow max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-3">Confirm Winner</h3>
            <p className="text-sm text-text-primary mb-4">
              This will create the prize payout and start the surety process. This action cannot be undone.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Doc Charge (₹)</label>
                <Input
                  type="number"
                  value={docChargeRupees}
                  onChange={(e) => setDocChargeRupees(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Tax Amount (₹)</label>
                <Input
                  type="number"
                  value={taxAmountRupees}
                  onChange={(e) => setTaxAmountRupees(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="pt-2 border-t border-border flex justify-between items-center">
                <span className="text-sm font-medium text-text-primary">Total Charge</span>
                <span className="text-sm font-bold text-text-primary">
                  <Money paise={new Decimal(docChargeRupees || 0).plus(new Decimal(taxAmountRupees || 0)).mul(100).toNumber()} />
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmWinner}
                disabled={confirmWinner.isPending}
                className="bg-success-500 hover:bg-success-600"
              >
                {confirmWinner.isPending ? 'Confirming...' : 'Confirm Winner'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel auction modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-surface-1 p-6 rounded shadow max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-3">Cancel Auction</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-1">Reason</label>
              <Input
                type="text"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter cancellation reason"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
              >
                Back
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelAuction}
                disabled={cancelAuction.isPending || !cancelReason.trim()}
              >
                {cancelAuction.isPending ? 'Cancelling...' : 'Cancel Auction'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuctionStatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    OPEN: 'bg-info-bg text-info-text',
    BIDDING_CLOSED: 'bg-warning-bg text-warning-text',
    WINNER_DETERMINED: 'bg-surface-2 text-text-primary',
    CONFIRMED: 'bg-success-bg text-success-text',
    CANCELLED: 'bg-danger-bg text-danger-text',
  };
  return (
    <div className="mb-4">
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colorMap[status] ?? 'bg-surface-2 text-text-primary'}`}>
        {STATUS_LABELS[status] ?? status}
      </span>
    </div>
  );
}

function NoAuctionState({ onOpen, isPending }: { onOpen: () => void; isPending: boolean }) {
  return (
    <Card className="text-center">
      <CardContent className="p-8">
        <p className="text-text-secondary mb-4">No auction has been opened for this cycle yet.</p>
        <Button
          variant="primary"
          onClick={onOpen}
          disabled={isPending}
        >
          {isPending ? 'Opening...' : 'Open Auction'}
        </Button>
      </CardContent>
    </Card>
  );
}

function WinnerDeterminedState({
  auction,
  sortedBids,
  onConfirmClick,
  isConfirming,
}: {
  auction: AuctionDetail;
  sortedBids: BidDetail[];
  onConfirmClick: () => void;
  isConfirming: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Summary card */}
      <Card className="border-border">
        <CardContent className="p-5">
          <h2 className="text-base font-semibold text-text-primary mb-4">Auction Summary</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Net Prize</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.netPrizeAmount != null ? formatPaise(auction.netPrizeAmount) : '—'}
              </p>
            </div>
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Dividend / Subscriber</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.perSubscriberDividend != null ? formatPaise(auction.perSubscriberDividend) : '—'}
              </p>
            </div>
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Foreman Commission</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.foremanCommission != null ? formatPaise(auction.foremanCommission) : '—'}
              </p>
            </div>
          </div>
          {auction.winningDiscount != null && (
            <p className="text-sm text-text-muted mt-3">
              Winning discount: {formatPaise(auction.winningDiscount)}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Bid table */}
      <AuctionBidLog
        bids={sortedBids}
        winnerSubscriptionId={auction.winnerSubscriptionId}
      />

      {/* Confirm action */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={onConfirmClick}
          disabled={isConfirming}
          className="bg-success-500 hover:bg-success-600"
        >
          {isConfirming ? 'Confirming…' : 'Confirm Winner'}
        </Button>
      </div>
    </div>
  );
}

function ConfirmedState({
  auction,
  sortedBids,
}: {
  auction: AuctionDetail;
  sortedBids: BidDetail[];
}) {
  return (
    <div className="space-y-6">
      {/* Payout status banner */}
      <div className="bg-success-bg border border-success-500 rounded p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold text-success-text">Winner Confirmed</p>
            <p className="text-sm text-success-text mt-1">
              Prize payout created — status: <span className="font-medium">PENDING_SURETY</span>
            </p>
            {auction.confirmedAt && (
              <p className="text-xs text-success-text mt-1">
                Confirmed at {new Date(auction.confirmedAt).toLocaleString('en-IN')}
              </p>
            )}
          </div>
          <Link
            to="/approvals"
            className="px-4 py-2 bg-success-500 text-white rounded text-sm hover:bg-success-600 whitespace-nowrap"
          >
            View Pending Approvals →
          </Link>
        </div>
      </div>

      {/* Summary card */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-base font-semibold text-text-primary mb-4">Auction Summary</h2>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Net Prize</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.netPrizeAmount != null ? formatPaise(auction.netPrizeAmount) : '—'}
              </p>
            </div>
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Dividend / Subscriber</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.perSubscriberDividend != null ? formatPaise(auction.perSubscriberDividend) : '—'}
              </p>
            </div>
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Foreman Commission</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.foremanCommission != null ? formatPaise(auction.foremanCommission) : '—'}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Doc Charge</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.docCharge != null ? formatPaise(auction.docCharge) : '—'}
              </p>
            </div>
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Tax Amount</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.taxAmount != null ? formatPaise(auction.taxAmount) : '—'}
              </p>
            </div>
            <div className="bg-surface-2 rounded p-3">
              <p className="text-xs text-text-muted mb-1">Total Charge</p>
              <p className="text-lg font-bold text-text-primary">
                {auction.totalCharge != null ? formatPaise(auction.totalCharge) : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bid table */}
      <AuctionBidLog
        bids={sortedBids}
        winnerSubscriptionId={auction.winnerSubscriptionId}
      />
    </div>
  );
}
